import { AnswersMetadata, Citation } from "features/assistant/definitions";

// Inline citation support for Answers messages, mirroring the standalone Answers
// chatbot: the streamed answer carries `<cite>id</cite>` markers that we turn into
// numbered links (`[N](#cite-<id>)`) resolved against the message's citation list.

export const CITE_LINK_PREFIX = "#cite-";

// Matches an inline citation marker and any leading whitespace, so a dropped marker
// does not leave a stray space before punctuation.
const CITE_MARKER = /[ \t]*<cite>([\s\S]*?)<\/cite>/g;

// Merge citations + references into the ordered, deduped list that drives both marker
// numbering and click resolution (first occurrence of a citation_id wins).
export function citationsFor(meta: AnswersMetadata): Citation[] {
  const merged = [...(meta.citations ?? []), ...(meta.references ?? [])];
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const c of merged) {
    const id = c.citation_id ?? undefined;
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(c);
  }
  return out;
}

// Lookup map for hover/click resolution, keyed by citation_id.
export function citationsById(meta: AnswersMetadata): Map<string, Citation> {
  const map = new Map<string, Citation>();
  for (const c of citationsFor(meta)) {
    if (c.citation_id) map.set(c.citation_id, c);
  }
  return map;
}

// Replace each `<cite>id</cite>` in the answer with a numbered markdown link. A marker
// whose id is not in the citation list is dropped (matches the standalone behavior), so
// ungrounded markers never render. Numbering follows citationsFor order.
export function numberCiteMarkers(
  content: string,
  citations: Citation[],
): string {
  if (!content) return content;
  const index = new Map<string, number>();
  citations.forEach((c, i) => {
    if (c.citation_id && !index.has(c.citation_id)) {
      index.set(c.citation_id, i + 1);
    }
  });
  return content.replace(CITE_MARKER, (_match, rawId: string) => {
    const id = rawId.trim();
    const n = index.get(id);
    if (!n) return "";
    return `[${n}](${CITE_LINK_PREFIX}${encodeURIComponent(id)})`;
  });
}

// Recover the citation_id from a `#cite-<id>` href produced by numberCiteMarkers.
export function citationIdFromHref(href?: string): string | null {
  if (!href?.startsWith(CITE_LINK_PREFIX)) return null;
  const raw = href.slice(CITE_LINK_PREFIX.length);
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

// Title shown for a citation, with a trailing ".pdf" stripped (matches the standalone).
export function citationLabel(citation: Citation): string {
  const title = citation.title ?? citation.citation_id ?? "Source";
  return title.replace(/\.pdf$/i, "");
}
